import { useState, useEffect } from 'react';
import axios from 'axios';

const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const usePushNotifications = () => {
    const [permission, setPermission] = useState<NotificationPermission>('default');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if ('Notification' in window) {
            setPermission(Notification.permission);
            checkSubscription();
        }
    }, []);

    const checkSubscription = async () => {
        if (!('serviceWorker' in navigator)) return;
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
        } catch (e) {
            console.error("Error checking subscription", e);
        }
    };

    const subscribe = async () => {
        if (!('serviceWorker' in navigator)) return;
        setLoading(true);
        try {
            const permissionResult = await Notification.requestPermission();
            setPermission(permissionResult);

            if (permissionResult !== 'granted') {
                setLoading(false);
                return;
            }

            // Fetch Public Key from backend
            // Note: Ensure configured proxy in vite.config.ts handles /api correctly
            const { data: publicKey } = await axios.get('/api/notifications/push/public-key');

            if (!publicKey) {
                console.error("No VAPID Public Key returned from server");
                setLoading(false);
                return;
            }

            const registration = await navigator.serviceWorker.ready;

            // Check if already subscribed but keys might have changed? 
            // Better to unsubscribe and subscribe again if keys mismatch, but simple logic first.
            let subscription = await registration.pushManager.getSubscription();

            if (!subscription) {
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey)
                });
            }

            // Send to backend
            const subJson = subscription.toJSON();

            await axios.post('/api/notifications/push/subscribe', {
                endpoint: subJson.endpoint,
                keys: subJson.keys,
                userAgent: navigator.userAgent
            });

            setIsSubscribed(true);
            console.log('Push subscription successful');

        } catch (error) {
            console.error('Failed to subscribe to push', error);
        } finally {
            setLoading(false);
        }
    };

    return { permission, isSubscribed, subscribe, loading };
};
